'use client';

import { useState, useEffect, useLayoutEffect, useMemo, useRef, memo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import DatePicker from 'react-datepicker';
import type { ShowsFilterFacets } from '@bip/api-client';
import { RATING_THRESHOLDS, SORT_OPTIONS, ShowsSortOption, browserTimeZone } from '../config';
import { useCurrency } from '../../_components/CurrencyProvider';
import { replaceShowsSearchParams, useShowsQueryParams } from '../_lib/useShowsQueryParams';

interface ShowsFiltersProps {
  facets: ShowsFilterFacets;
  /** True once the current result set is confirmed empty (0 records, not
   *  still loading) — every filter/sort button disables and any open
   *  dropdown auto-closes, matching Sports' `filtersDisabled`. */
  disabled?: boolean;
  /** DOCS/shows/image copy.png's exact sequence: filter buttons row, THEN
   *  this heading ("{City} Theaters - {N} shows found"), THEN the active-
   *  filter chips + Reset row — rendered here (not by the parent
   *  `ShowsList`, before/after this whole component) specifically so it
   *  lands BETWEEN those two rows, which live inside this one component. */
  resultsHeading?: ReactNode;
}

type DropdownKey = 'dateRange' | 'price' | 'rating' | 'category' | 'sortBy' | null;

function splitCsv(value: string | null): string[] {
  return (value || '').split(',').map((v) => v.trim()).filter(Boolean);
}

function toggleInSet(set: Set<string>, val: string): Set<string> {
  const next = new Set(set);
  if (next.has(val)) next.delete(val);
  else next.add(val);
  return next;
}

import {
  todayInDateStr,
  computeWeekendWindow,
  dateStrToLocalDate,
  localDateToDateStr,
  rangeDayClassName,
  formatChipDate,
} from '../_lib/dateUtils';


// ─── Module-level helpers/components ────────────────────────────────────────
// Ported from Sports' EventsFilters.tsx: these must NOT be defined inside
// ShowsFilters. A component defined inside a parent gets a new function
// reference on every render — React treats that as a different component
// *type* and fully unmounts/remounts it, which resets scroll position and
// drops focus/click-handler state on every keystroke/checkbox click.

/**
 * Double-range price slider — two overlapping native `<input type="range">`
 * thumbs over a gradient track, same technique/markup as Sports'
 * `PriceSlider`. Like Sports, shows-service returns prices in the venue's
 * currency (defaults to GBP — see shows-search.service.ts), so the parent
 * converts GBP↔user-currency at the boundary (see `gbpToUser`/`userToGbp`
 * below); this component just renders whatever range/currencySymbol it's
 * given. `memo`-wrapped so dragging never re-renders the parent (and
 * therefore never blinks the dropdown).
 */
const PriceSlider = memo(function PriceSlider({
  title,
  rangeMin,
  rangeMax,
  initialMin,
  initialMax,
  currencySymbol,
  onApply,
  onClear,
  onClose,
}: {
  title?: string;
  rangeMin: number;
  rangeMax: number;
  initialMin: number;
  initialMax: number;
  currencySymbol: string;
  onApply: (min: string, max: string) => void;
  onClear: () => void;
  onClose?: () => void;
}) {
  const [localMin, setLocalMin] = useState(initialMin);
  const [localMax, setLocalMax] = useState(initialMax);

  // Facets load asynchronously after mount — when the real price range
  // arrives, initialMin/initialMax change; sync local state so the slider
  // reflects the actual data range rather than a stale/fallback one.
  useEffect(() => {
    setLocalMin(initialMin);
    setLocalMax(initialMax);
  }, [initialMin, initialMax]);

  const pct = (v: number) => (rangeMax > rangeMin ? ((v - rangeMin) / (rangeMax - rangeMin)) * 100 : 0);

  return (
    <div className="double-slider-box">
      {title && onClose && (
        <div className="filter-sheet-header">
          <span className="filter-sheet-title">{title}</span>
          <button type="button" className="filter-sheet-close" onClick={onClose} aria-label={`Close ${title}`}>
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      )}
      <div className="price-range-text">
        <div className="min-box">
          <span>{currencySymbol}</span>
          <span>{Math.round(localMin)}</span>
        </div>
        <span>–</span>
        <div className="max-box">
          <span>{currencySymbol}</span>
          <span>{Math.round(localMax)}</span>
        </div>
      </div>
      <div className="range-slider">
        <span
          className="slider-track"
          style={{
            background: `linear-gradient(to right, #e6dff0 ${pct(localMin)}%, var(--color-primary-hover, #dc9cf7) ${pct(localMin)}%, var(--color-primary-hover, #dc9cf7) ${pct(localMax)}%, #e6dff0 ${pct(localMax)}%)`,
          }}
        />
        <input
          type="range"
          min={rangeMin}
          max={rangeMax}
          value={localMin}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v <= localMax) setLocalMin(v);
          }}
        />
        <input
          type="range"
          min={rangeMin}
          max={rangeMax}
          value={localMax}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (v >= localMin) setLocalMax(v);
          }}
        />
      </div>
      <div className="filter-dropdown-footer">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setLocalMin(rangeMin);
            setLocalMax(rangeMax);
            onClear();
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onApply(String(Math.round(localMin)), String(Math.round(localMax)))}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

/** Shared multi-select checkbox list + Clear/Apply footer — used by both
 *  Rating and Category. Matches Sports' `MultiSelectPanel`. */
function CheckboxPanel({
  title,
  items,
  pending,
  onToggle,
  onClear,
  onApply,
  onClose,
}: {
  title?: string;
  items: { value: string; label: string; count?: number }[];
  pending: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  onApply: () => void;
  onClose?: () => void;
}) {
  return (
    <>
      {title && onClose && (
        <div className="filter-sheet-header">
          <span className="filter-sheet-title">{title}</span>
          <button type="button" className="filter-sheet-close" onClick={onClose} aria-label={`Close ${title}`}>
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-gray-text px-3 py-2">No options available</p>
      ) : (
        <div className="filter-checkbox-scroll">
          <ul>
            {items.map((item) => (
              <li key={item.value}>
                <label className="filter-checkbox-item">
                  <input type="checkbox" checked={pending.has(item.value)} onChange={() => onToggle(item.value)} />
                  <span className="item-label">{item.label}</span>
                  {item.count !== undefined && <span className="item-count">({item.count})</span>}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="filter-dropdown-footer">
        <button type="button" className="btn btn-secondary" onClick={onClear}>
          Clear
        </button>
        <button type="button" className="btn btn-primary" onClick={onApply}>
          Apply
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Filters + sort + applied-filter chips — ported 1:1 from the Sports domain
 * (`app/sports/_components/EventsFilters.tsx`) per explicit request: "Design
 * of filters on shows listing page should be same like we have sports
 * filter and functionality also should be same." Mechanics carried over
 * verbatim:
 *  - Single `openDropdown` state (not independent `<details>` elements) —
 *    only one panel open at a time.
 *  - Outside-click-to-close via one `document mousedown` listener.
 *  - Auto flip-up (`.open-up`) when a panel would overflow the viewport
 *    bottom, re-measured on open and on window resize.
 *  - Mobile (`≤767px`) bottom-sheet: the open panel + a backdrop get
 *    `position: fixed` etc. stamped on as inline `!important` styles via
 *    `useLayoutEffect` — sidesteps CSS specificity fights the same way
 *    Sports' version does.
 *  - Multi-select (Rating/Category) and Price use pending/draft state,
 *    committed to the URL only on the panel's own Clear/Apply.
 *  - `disabled` (all filter/sort buttons grey out + any open panel closes)
 *    when the current result set is confirmed empty.
 *  - Applied-filter chips + "Reset" render INLINE here, directly under the
 *    button row — NOT as a separate component rendered after the results
 *    heading (a deliberate reversal of the prior `ShowsActiveFilters`
 *    split, to match Sports' layout, where chips live inside the filters
 *    section).
 *
 * Deliberately NOT ported: Sports' non-URL local-only sort state (Shows
 * keeps `sortBy` in the URL, its existing/working convention) and Sports'
 * `history.replaceState` + CustomEvent navigation transport (Shows keeps
 * `router.push`, already working end-to-end here). Also not ported: Sports'
 * Date-preset list (Today/Next 7 Days/This Month) — Shows already has
 * "Today"/"This Weekend" as their own top-level toggle pills per the
 * brd-backend spec, so folding them into the Date dropdown too would just
 * duplicate them; the Date dropdown stays a plain From/To range.
 */
export function ShowsFilters({ facets, disabled = false, resultsHeading }: ShowsFiltersProps) {
  const pathname = usePathname();
  const searchParams = useShowsQueryParams();
  const { currencySymbol, currencyCode, exchangeRates, convertPrice } = useCurrency();

  // shows-service returns prices in the venue's currency (defaults to GBP —
  // see shows-search.service.ts). Convert the price range for display
  // (GBP → user currency) and convert back when applying the filter (user
  // currency → GBP) so the backend receives values it can compare against
  // the raw GBP prices stored in the database.
  const gbpToUser = (gbp: number): number => convertPrice(gbp, 'GBP');

  // Reverse: user-currency amount → GBP for URL params sent to backend.
  // exchangeRates['GBP'] = how many GBP equal 1 unit of user currency.
  // When userCurrency IS GBP there is no self-rate → falls back to 1 (no-op).
  const userToGbp = (user: number): number =>
    currencyCode === 'GBP' ? user : Math.round(user * (exchangeRates['GBP'] || 1) * 100) / 100;

  const activeCategories = splitCsv(searchParams.get('category'));
  // Restricted to the bands the filter actually offers (`RATING_THRESHOLDS`). A value outside them
  // can only come from a stale bookmark/link — notably `?rating=5`, from before "5+" was folded
  // into the top "4+" band — and would otherwise render a "5+ Stars" chip for a band with no
  // checkbox behind it, while the backend ignores it. Same guard as `isOfferedRatingThreshold`
  // in `shows-search.service.ts`.
  const activeRatings = splitCsv(searchParams.get('rating'))
    .map(Number)
    .filter((n) => (RATING_THRESHOLDS as readonly number[]).includes(n));
  const priceMinParam = searchParams.get('priceMin');
  const priceMaxParam = searchParams.get('priceMax');
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  const today = searchParams.get('today') === 'true';
  const weekend = searchParams.get('weekend') === 'true';
  const sortBy = (searchParams.get('sortBy') as ShowsSortOption) || 'recommended';

  // The Date dropdown must stay visually synchronized with Today/This Weekend — those two toggles
  // set `today=true`/`weekend=true` and DELETE `dateFrom`/`dateTo` (mutually exclusive), so reading
  // only `dateFrom`/`dateTo` for the picker's own selected range left it showing nothing/stale while
  // Today/Weekend was active. Resolves to the SAME window the backend actually filtered on (reuses
  // this file's own `computeWeekendWindow`/`todayInDateStr`, already used for the chip label).
  const effectiveDateRange = useMemo(() => {
    if (dateFrom) return { from: dateFrom, to: dateTo || dateFrom };
    const tz = browserTimeZone();
    if (today) {
      const t = todayInDateStr(tz);
      return { from: t, to: t };
    }
    if (weekend) {
      const { start, end } = computeWeekendWindow(todayInDateStr(tz));
      return { from: start, to: end };
    }
    return null;
  }, [dateFrom, dateTo, today, weekend]);

  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const openDropdownRef = useRef<DropdownKey>(null);
  openDropdownRef.current = openDropdown;
  const containerRef = useRef<HTMLDivElement>(null);

  const [pendingCategories, setPendingCategories] = useState<Set<string>>(new Set(activeCategories));
  const [pendingRatings, setPendingRatings] = useState<Set<string>>(new Set(activeRatings.map(String)));
  // Same range-capable picker as Hotels' SearchBar (`selectsRange`,
  // `monthsShown={2}`, inline) — but unlike Hotels, a single click with no
  // second date picked is a VALID end state here (dateFrom alone means
  // "exactly this day" per shows-search.service.ts's own dateFrom/dateTo
  // comment) — there's no `resolveDateRange`-style same-day bump forcing a
  // second day, since Hotels' check-in/check-out can never be one day but a
  // show date filter legitimately can be.
  const [draftDateFrom, setDraftDateFrom] = useState<Date | null>(
    effectiveDateRange ? dateStrToLocalDate(effectiveDateRange.from) : null,
  );
  const [draftDateTo, setDraftDateTo] = useState<Date | null>(
    effectiveDateRange ? dateStrToLocalDate(effectiveDateRange.to) : null,
  );

  // Re-sync drafts whenever the URL changes underneath us (e.g. a chip ✕
  // click updates the URL directly, bypassing this component's own Apply).
  useEffect(() => {
    setPendingCategories(new Set(activeCategories));
    setPendingRatings(new Set(activeRatings.map(String)));
    setDraftDateFrom(effectiveDateRange ? dateStrToLocalDate(effectiveDateRange.from) : null);
    setDraftDateTo(effectiveDateRange ? dateStrToLocalDate(effectiveDateRange.to) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 767);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close every open dropdown once the result set is confirmed empty.
  useEffect(() => {
    if (disabled) setOpenDropdown(null);
  }, [disabled]);

  function pushParams(mutate: (params: URLSearchParams) => void) {
    const params = new URLSearchParams(searchParams.toString());
    mutate(params);
    params.delete('page');
    // Every filter/sort action (Today, Weekend, category, rating, date
    // range, sort) goes through this one helper — see
    // `useShowsQueryParams.ts`'s doc comment for why this bypasses
    // `router.push` entirely rather than just passing `{ scroll: false }`.
    replaceShowsSearchParams(pathname, params);
  }

  function toggleDropdown(key: DropdownKey) {
    setOpenDropdown((prev) => (prev === key ? null : key));
  }

  function applyCategory() {
    pushParams((p) => {
      if (pendingCategories.size) p.set('category', [...pendingCategories].join(','));
      else p.delete('category');
    });
    setOpenDropdown(null);
  }

  function applyRating() {
    pushParams((p) => {
      if (pendingRatings.size) p.set('rating', [...pendingRatings].join(','));
      else p.delete('rating');
    });
    setOpenDropdown(null);
  }

  function applyDate(explicit?: { from: Date | null; to: Date | null }) {
    const from = explicit ? explicit.from : draftDateFrom;
    const to = explicit ? explicit.to : draftDateTo;
    pushParams((p) => {
      if (from) {
        p.set('dateFrom', localDateToDateStr(from));
        p.set('dateTo', localDateToDateStr(to || from));
      } else {
        p.delete('dateFrom');
        p.delete('dateTo');
      }
      p.delete('today');
      p.delete('weekend');
    });
    setOpenDropdown(null);
  }

  function toggleToday() {
    pushParams((p) => {
      if (today) p.delete('today');
      else {
        p.set('today', 'true');
        p.delete('weekend');
        p.delete('dateFrom');
        p.delete('dateTo');
      }
    });
  }

  function toggleWeekend() {
    pushParams((p) => {
      if (weekend) p.delete('weekend');
      else {
        p.set('weekend', 'true');
        p.delete('today');
        p.delete('dateFrom');
        p.delete('dateTo');
      }
    });
  }

  function setSortBy(key: string) {
    pushParams((p) => {
      if (key === 'recommended') p.delete('sortBy');
      else p.set('sortBy', key);
    });
    setOpenDropdown(null);
  }

  function removeChip(key: string, value?: string) {
    pushParams((p) => {
      if (key === 'category' && value) {
        const next = activeCategories.filter((c) => c !== value);
        if (next.length) p.set('category', next.join(','));
        else p.delete('category');
      } else if (key === 'rating' && value) {
        const next = activeRatings.filter((r) => String(r) !== value);
        if (next.length) p.set('rating', next.join(','));
        else p.delete('rating');
      } else if (key === 'price') {
        p.delete('priceMin');
        p.delete('priceMax');
      } else if (key === 'date') {
        p.delete('dateFrom');
        p.delete('dateTo');
      } else if (key === 'today') {
        p.delete('today');
      } else if (key === 'weekend') {
        p.delete('weekend');
      }
    });
  }

  function clearOne(keys: string[]) {
    pushParams((p) => {
      keys.forEach((k) => p.delete(k));
    });
  }

  function fullReset() {
    pushParams((p) => {
      p.delete('category');
      p.delete('rating');
      p.delete('priceMin');
      p.delete('priceMax');
      p.delete('dateFrom');
      p.delete('dateTo');
      p.delete('today');
      p.delete('weekend');
      p.delete('sortBy');
    });
  }

  // Auto flip-up (`.open-up`) when a dropdown would overflow the viewport bottom (desktop only).
  useLayoutEffect(() => {
    if (!openDropdown || isMobile) return;
    const reMeasure = () => {
      const panels = containerRef.current?.querySelectorAll('.filter-dropdown-panel');
      if (!panels) return;
      panels.forEach((panel) => {
        const el = panel as HTMLElement;
        el.classList.remove('open-up');
        const rect = el.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) el.classList.add('open-up');
      });
    };
    const timer = setTimeout(reMeasure, 10);
    window.addEventListener('resize', reMeasure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', reMeasure);
    };
  }, [openDropdown, isMobile]);

  // Close dropdown on outside click (but not clicks inside the open panel,
  // and not clicks on a filter/sort trigger button itself — those already
  // toggle correctly on their own `onClick`, including switching straight
  // from one open dropdown to another via `toggleDropdown`'s `prev === key`
  // check. Also closing here on the SAME mousedown would race the
  // subsequent click's toggle: the close-then-reopen sequence sometimes
  // left the browser not firing `click` at all on the newly-targeted
  // button — confirmed live via raw coordinate-based clicks, not just a
  // Playwright artifact — so trigger buttons are excluded entirely rather
  // than relying on both handlers cooperating within the same event.
  useEffect(() => {
    function handler(e: MouseEvent | TouchEvent) {
      const target = e.target as HTMLElement;
      if (target?.closest?.('.filter-dropdown-panel')) return;
      if (target?.closest?.('.filter-btn, .sort-btn')) return;
      if (openDropdownRef.current !== null) setOpenDropdown(null);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Entirely data-driven — `facets.categories` (backend `buildFacets()`) already only contains
  // categories with ≥1 matching show in the current filtered set, alphabetically sorted. No static
  // fallback list unioned in anymore (there used to be one here) — a category with zero matching
  // shows must not render as a checkbox option at all.
  const categoryOptions = useMemo(
    () => facets.categories.map((c) => ({ value: c.value, label: c.value, count: c.count })),
    [facets.categories],
  );

  // Same "only render if it actually has matching shows" rule as categories — `facets.ratingThresholds`
  // now carries per-BAND counts (see `ratingInBucket` in `shows-search.service.ts`), so a threshold
  // with 0 matching shows in the current filtered set is dropped entirely instead of always showing.
  const ratingFacetCounts = new Map(facets.ratingThresholds.map((r) => [r.value, r.count]));
  const ratingOptions = RATING_THRESHOLDS.filter((t) => (ratingFacetCounts.get(t) || 0) > 0).map((t) => ({
    value: String(t),
    label: `${t}+ Star`,
    count: ratingFacetCounts.get(t) || 0,
  }));

  const activeSortOption = SORT_OPTIONS.find((o) => o.value === sortBy) ?? SORT_OPTIONS[0];

  // Slider operates in the user's DISPLAY currency; facets.priceBounds and
  // priceMin/priceMax URL params stay in GBP (what the backend compares
  // against), converted at this boundary.
  const priceRangeMin = Math.floor(gbpToUser(facets.priceBounds.min));
  const priceRangeMax = Math.ceil(gbpToUser(facets.priceBounds.max));
  const priceInitialMin = priceMinParam ? Math.round(gbpToUser(Number(priceMinParam))) : priceRangeMin;
  const priceInitialMax = priceMaxParam ? Math.round(gbpToUser(Number(priceMaxParam))) : priceRangeMax;

  // Auto-close any open dropdown when all filters are disabled (e.g. empty results).
  useEffect(() => {
    if (disabled) setOpenDropdown(null);
  }, [disabled]);

  // Lock body & document scroll on mobile while a dropdown panel is open — prevents
  // background cards from scrolling when dragging the slider/checkbox list.
  useEffect(() => {
    if (!openDropdown || typeof document === 'undefined') return;
    const isMobileView = window.innerWidth <= 767;
    if (!isMobileView) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [openDropdown]);

  // ──────── Active Chips ────────
  const chips: { key: string; label: string; value?: string }[] = [];

  if (today) {
    chips.push({ key: 'today', label: 'Today' });
  } else if (weekend) {
    chips.push({ key: 'weekend', label: 'This Weekend' });
  } else if (dateFrom) {
    const label = dateTo && dateFrom !== dateTo ? `${formatChipDate(dateFrom)} – ${formatChipDate(dateTo)}` : formatChipDate(dateFrom);
    chips.push({ key: 'date', label });
  }

  if (priceMinParam || priceMaxParam) {
    chips.push({
      key: 'price',
      label: `${currencySymbol}${priceInitialMin} – ${currencySymbol}${priceInitialMax}`,
    });
  }

  activeCategories.forEach((cat) => {
    chips.push({ key: 'category', label: cat, value: cat });
  });

  activeRatings.forEach((rating) => {
    chips.push({ key: 'rating', label: `${rating}+ Stars`, value: String(rating) });
  });

  const FilterButton = ({
    dropKey,
    label,
    active,
    hasCaret = true,
  }: {
    dropKey: DropdownKey;
    label: string;
    active: boolean;
    hasCaret?: boolean;
  }) => (
    <button
      type="button"
      onClick={() => toggleDropdown(dropKey)}
      disabled={disabled}
      className={`filter-btn ${active ? 'active' : ''} ${openDropdown === dropKey ? 'open' : ''}`}
    >
      {label}
      {hasCaret && <i className="fa-solid fa-caret-down ml-1" aria-hidden />}
    </button>
  );

  function renderDropdownPanel(key: DropdownKey) {
    if (!key) return null;
    switch (key) {
      case 'dateRange':
        return (
          <div className="filter-dropdown-panel wide no-scroll">
            <DatePicker
              selected={draftDateFrom}
              onChange={(dates) => {
                const [start, end] = dates as [Date | null, Date | null];
                setDraftDateFrom(start);
                setDraftDateTo(end);
              }}
              startDate={draftDateFrom}
              endDate={draftDateTo}
              selectsRange
              minDate={new Date()}
              monthsShown={2}
              dayClassName={(date) => rangeDayClassName(date, draftDateFrom, draftDateTo)}
              inline
            />
            <div className="filter-dropdown-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setDraftDateFrom(null);
                  setDraftDateTo(null);
                  applyDate({ from: null, to: null });
                }}
              >
                Clear
              </button>
              <button type="button" className="btn btn-primary" onClick={() => applyDate()}>
                Apply
              </button>
            </div>
          </div>
        );

      case 'price':
        return (
          <div className="filter-dropdown-panel wide no-scroll">
            <PriceSlider
              title="Price"
              rangeMin={priceRangeMin}
              rangeMax={priceRangeMax}
              initialMin={priceInitialMin}
              initialMax={priceInitialMax}
              currencySymbol={currencySymbol}
              onApply={(min, max) => {
                pushParams((p) => {
                  p.set('priceMin', String(Math.floor(userToGbp(Number(min)))));
                  p.set('priceMax', String(Math.ceil(userToGbp(Number(max)))));
                });
                setOpenDropdown(null);
              }}
              onClear={() => {
                clearOne(['priceMin', 'priceMax']);
                setOpenDropdown(null);
              }}
              onClose={() => setOpenDropdown(null)}
            />
          </div>
        );

      case 'rating':
        return (
          <div className="filter-dropdown-panel">
            <CheckboxPanel
              title="Rating"
              items={ratingOptions}
              pending={pendingRatings}
              onToggle={(v) => setPendingRatings((prev) => toggleInSet(prev, v))}
              onClear={() => {
                setPendingRatings(new Set());
                clearOne(['rating']);
                setOpenDropdown(null);
              }}
              onApply={applyRating}
              onClose={() => setOpenDropdown(null)}
            />
          </div>
        );

      case 'category':
        return (
          <div className="filter-dropdown-panel">
            <CheckboxPanel
              title="Category"
              items={categoryOptions}
              pending={pendingCategories}
              onToggle={(v) => setPendingCategories((prev) => toggleInSet(prev, v))}
              onClear={() => {
                setPendingCategories(new Set());
                clearOne(['category']);
                setOpenDropdown(null);
              }}
              onApply={applyCategory}
              onClose={() => setOpenDropdown(null)}
            />
          </div>
        );

      case 'sortBy':
        return (
          <div className="filter-dropdown-panel" style={isMobile ? undefined : { left: 'auto', right: 0 }}>
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">Sorted by</span>
              <button
                type="button"
                className="filter-sheet-close"
                onClick={() => setOpenDropdown(null)}
                aria-label="Close Sort"
              >
                <i className="fa-solid fa-xmark" aria-hidden />
              </button>
            </div>
            <ul>
              {SORT_OPTIONS.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    className={`filter-option ${opt.value === sortBy ? 'selected' : ''}`}
                    onClick={() => setSortBy(opt.value)}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );

      default:
        return null;
    }
  }

  return (
    <div className="position-relative" ref={containerRef}>
      {/* Mobile-only bottom-sheet portal — rendered directly into document.body so it escapes
          .shows-filter-tabs's overflow-x: auto and -webkit-overflow-scrolling: touch clipping,
          ensuring filter panels open and display completely and reliably on iPhone/mobile. */}
      {isMobile && openDropdown && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className="filter-mobile-overlay"
            aria-hidden="true"
            onClick={() => setOpenDropdown(null)}
          />
          {renderDropdownPanel(openDropdown)}
        </>,
        document.body,
      )}

      <div className="shows-filter-row pt-4">
        <div className="shows-filter-tabs">
          {/* Date filter */}
          <div className="position-relative">
            {/* `active` stays keyed on an EXPLICIT `dateFrom` only, not Today/This Weekend — those
                two already have their own separate active-pill highlight; the calendar dropdown
                itself still shows the resolved Today/Weekend dates selected when opened (see
                `effectiveDateRange` above), this button just doesn't ALSO highlight for them. */}
            <FilterButton dropKey="dateRange" label="Date" active={!!dateFrom} hasCaret={false} />
            {!isMobile && openDropdown === 'dateRange' && renderDropdownPanel('dateRange')}
          </div>

          {/* Price filter */}
          <div className="position-relative">
            <FilterButton
              dropKey="price"
              label={
                priceMinParam || priceMaxParam
                  ? `${currencySymbol}${priceInitialMin} – ${currencySymbol}${priceInitialMax}`
                  : 'Price'
              }
              active={!!(priceMinParam || priceMaxParam)}
            />
            {!isMobile && openDropdown === 'price' && renderDropdownPanel('price')}
          </div>

          {/* Rating filter — multi-select */}
          <div className="position-relative">
            <FilterButton
              dropKey="rating"
              label={activeRatings.length ? `${[...activeRatings].sort((a, b) => b - a).join('+, ')}+ Stars` : 'Rating'}
              active={activeRatings.length > 0}
            />
            {!isMobile && openDropdown === 'rating' && renderDropdownPanel('rating')}
          </div>

          {/* Category filter — multi-select, alphabetical, with count */}
          <div className="position-relative">
            <FilterButton
              dropKey="category"
              label={activeCategories.length ? activeCategories.join(', ') : 'Category'}
              active={activeCategories.length > 0}
            />
            {!isMobile && openDropdown === 'category' && renderDropdownPanel('category')}
          </div>

          {/* Today / This Weekend — plain toggle pills, no dropdown, apply immediately */}
          <div className="position-relative">
            <button
              type="button"
              onClick={toggleToday}
              disabled={disabled}
              className={`filter-btn ${today ? 'active' : ''}`}
            >
              Today
            </button>
          </div>
          <div className="position-relative">
            <button
              type="button"
              onClick={toggleWeekend}
              disabled={disabled}
              className={`filter-btn ${weekend ? 'active' : ''}`}
            >
              This Weekend
            </button>
          </div>
        </div>

        {/* Sort */}
        <div className="shows-sort-wrap">
          <button
            type="button"
            onClick={() => toggleDropdown('sortBy')}
            disabled={disabled}
            className={`sort-btn ${openDropdown === 'sortBy' ? 'open' : ''}`}
          >
            Sorted by : <span className="font-bold">{activeSortOption.label}</span>
            <i className="fa-sharp fa-light fa-arrow-down-arrow-up" aria-hidden />
          </button>
          {!isMobile && openDropdown === 'sortBy' && renderDropdownPanel('sortBy')}
        </div>
      </div>

      {resultsHeading}

      {/* Active filter chips + Reset — inline in the filters section, matching Sports. */}
      {chips.length > 0 && (
        <div className="filter-chips">
          {chips.map((chip, i) => (
            <span key={`${chip.key}-${chip.value || i}`} className="filter-chip">
              {chip.label}
              <button
                type="button"
                onClick={() => removeChip(chip.key, chip.value)}
                className="chip-close"
                aria-label={`Remove ${chip.key} filter`}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </span>
          ))}
          <button type="button" onClick={fullReset} className="clear-all-btn">
            Reset
          </button>
        </div>
      )}
    </div>
  );
}
